import { FC, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useRecoilState, useRecoilValue } from 'recoil';
import { taskAtom } from 'packages/at-shell/state/recoil/task';
import { useTaskSnapshotSWR } from 'packages/at-shell/components/app-shell/task-annotation-history-api';
import { useChangeURLParams } from 'packages/lib/utils/url';
import { RadioList, RadioListItem } from 'packages/lib/common-ui/radio-list';
import { FloatingWindow } from 'packages/lib/common-ui/floating-window';
import { Alert, Skeleton, Tooltip, Typography, theme } from 'antd';
import { annotationHistoryDialogIsOpenAtom } from 'packages/at-shell/state/recoil/ui';
import { useNonNullQueryParams } from 'packages/lib/utils/hooks/use-query';
import { useLocation } from 'react-router-dom';
import { z } from 'zod';

/** To add Task Annotation History to any app:
 1. Add an 'Annotation History...' item to the Profile menu for that app. When the user selects it, set the annotationHistoryDialogIsOpenAtom atom to true.
 2. Modify the annotation tool to use annotations from its normal API endpoint when snapshotToken param is not present in the URL, and otherwise use values
    from the relevant snapshot. See useCurrentLensAnnotations in TLD tool for an example. In the case of useCurrentLensAnnotations: all code that formerly
    called useLensAnnotationsSWR was modified to call useCurrentLensAnnotations instead, which resulted in snapshots 'just working'.
 3. Lock the tool's UI and keyboard shortcuts, for any UI that adds/updates/deletes annotations (including Undo/Redo), when a snapshotToken is present in the URL.
 */

export const TaskAnnotationHistory: FC = () => {
  const {
    token: { colorTextDescription },
  } = theme.useToken();
  const changeURLParams = useChangeURLParams();
  const urlParamsObj = useNonNullQueryParams<{
    snapshotToken: string | undefined;
  }>();
  const task = useRecoilValue(taskAtom);
  const {
    data: taskSnapshots,
    isLoading: isLoadingTaskSnapshots,
    error: taskSnapshotError,
  } = useTaskSnapshotSWR(task?.token, z.any());

  const snapshotsReversed = taskSnapshots ? [...taskSnapshots].reverse() : [];

  const items = snapshotsReversed
    ? snapshotsReversed.map((item) => (
        <Tooltip
          key={'annotation-history-snapshot-item-' + item.token}
          zIndex={9001}
          placement="right"
          title={
            <div style={{ padding: '1em 1em' }}>
              <p>User: {item.lastUpdatedBy}</p>
              <br />
              <p>
                Date: {format(new Date(item.createdAt), 'MMM d, yyyy, hh:mm a')}
              </p>
            </div>
          }
        >
          <div>
            <RadioListItem>
              <Typography style={{ fontWeight: 450 }}>
                {item.oldAnnotationState}{' '}
              </Typography>
              <Typography style={{ color: colorTextDescription }}>
                (to {item.newAnnotationState})
              </Typography>
            </RadioListItem>
          </div>
        </Tooltip>
      ))
    : [];
  items.unshift(
    <RadioListItem>
      <Typography style={{ fontWeight: 450 }}>Current</Typography>
    </RadioListItem>
  );

  const onChange = (listIdx: number) => {
    if (!snapshotsReversed) return;
    if (listIdx === 0) {
      // User clicked 'Current' item
      changeURLParams({ snapshotToken: null });
    } else {
      changeURLParams({ snapshotToken: snapshotsReversed[listIdx - 1].token });
    }
  };

  const curSnapshotIdx =
    snapshotsReversed?.findIndex(
      (snapshot) => snapshot.token === urlParamsObj.snapshotToken
    ) ?? -1;

  return (
    <>
      {isLoadingTaskSnapshots ? (
        <Skeleton active title={false} paragraph={{ rows: 6 }} />
      ) : (
        <RadioList
          curSelectionIdx={curSnapshotIdx + 1} // + 1 since 'Current' is added to top of UI list, but is not in Snapshots
          items={items}
          onChange={onChange}
        />
      )}
      {taskSnapshotError && !isLoadingTaskSnapshots && (
        <Alert
          type="error"
          message="Could not load Annotation History"
          description="An error occurred when attempting to load Annotation History"
        />
      )}
    </>
  );
};

export const TaskAnnotationHistoryWindow: FC = () => {
  const [isOpen, setIsOpen] = useRecoilState(annotationHistoryDialogIsOpenAtom);
  const changeURLParams = useChangeURLParams();
  const [prevPathname, setPrevPathname] = useState('');
  const location = useLocation();
  const urlParamsObj = useNonNullQueryParams<{
    snapshotToken: string | undefined;
  }>();

  // If user loads the tool with a URL that was shared with a snapshotToken already in the URL, the Annotation History floating dialog should automatically open
  useEffect(() => {
    if (urlParamsObj.snapshotToken) {
      setIsOpen(true);
    }
  }, [urlParamsObj.snapshotToken, setIsOpen]);

  // Bug fix: navigating to a different tool should close the Annotation History floating window (which is part of at-shell DOM, so could otherwise re-appear on load)
  useEffect(() => {
    if (location.pathname !== prevPathname && !urlParamsObj.snapshotToken) {
      setIsOpen(false);
    }
    setPrevPathname(location.pathname);
  }, [location.pathname, urlParamsObj.snapshotToken, prevPathname, setIsOpen]);

  return (
    <FloatingWindow
      title="Annotation History"
      minWidth={320}
      isOpen={isOpen}
      onClose={() => {
        setIsOpen(false);
        changeURLParams({ snapshotToken: null });
      }}
    >
      <TaskAnnotationHistory />
    </FloatingWindow>
  );
};
